"use client";

import React, { useEffect, useCallback, useMemo } from "react";
import { useCheckoutTrial } from "../hooks/use-checkout-trial";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { DateSelectionStep } from "../date-selection-step";
import { TicketSelectionStep } from "../ticket-selection-step";
import { UserFormStep } from "../user-form-step";
import { ParticipantsStep, participantsToPayload } from "../participants-step";
import type {
  CheckoutParticipantField,
  ExistingParticipant,
} from "../participants-step";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { ProgressBar } from "../progress-bar";
import { ToastProvider } from "../checkout-toast";
import { formatDate, formatTimeRange, scrollCheckoutContentToTop } from "../utils";
import { buildFallbackBookingPolicy } from "../utils/build-fallback-policy";
import { resolveSummaryLocationName } from "../utils/session-location";
import type { DiscountResult } from "../utils/types";

export type CheckoutDropInProps = {
  // Data
  course: any;
  sessions?: any[];
  ticketType: any; // Single ticket type for drop-in
  user?: any;
  bundleID?: string;
  bundle?: any;
  locations?: any[];
  tutors?: any[];
  /** Pre-select a session by ID (skips date selection step) */
  initialSessionId?: string;

  // Callbacks
  onLoadMoreDates: (args: { page: number; limit: number }) => Promise<any>;
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void;
  onJoinWaitlist?: (session?: any) => void;

  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;
  isSendingMaterial?: boolean;

  // School/Business settings
  school?: {
    name?: string;
    _id?: string;
    isNewsletterSignupEnabled?: boolean;
    customNewsletterSignupText?: string | null;
    businessCountry?: string;
  };
  allowPrefillOthersGuestsOnCheckout?: boolean;
  // Participants (attendees) — threaded from the orchestrator/widget.
  participantsEnabled?: boolean;
  participantLabel?: string;
  participantFields?: CheckoutParticipantField[];
  existingParticipants?: ExistingParticipant[];
  buyerIsAttendee?: boolean;
  /** Name-later listing: the attendee step accepts empty places. */
  allowUnnamedAttendees?: boolean;
  /** The signed-in customer's identity, for naming the buyer on their own card. */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Answers the buyer has already given about themselves elsewhere in this basket. */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** When true, hides back navigation to options/dates (timetable Book Now) */
  isDirectBooking?: boolean;

  // Basket feature
  /** Current number of items in the basket */
  basketItemCount?: number;
  /**
   * Per-session quantity this customer has already added to their basket
   * for this course. Subtracted from displayed "spots left".
   */
  reservedSessionQuantities?: Record<string, number>;
  /** Callback when user clicks "Add to Basket" */
  onAddToBasket?: (itemData: {
    type: "booking";
    subType: "dropin";
    courseId: string;
    courseName: string;
    sessionId: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /** Callback when user clicks "Proceed to Checkout" in BasketChoiceStep — adds item to basket and navigates to basket checkout page */
  onProceedToBasketCheckout: (itemData: {
    type: "booking";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /** Callback to trigger login flow. When provided and user is not logged in, shows "Log in" prompt in payment methods. */
  onLogin?: () => void;
  /**
   * When true, skip the basket choice step and auto-fire onProceedToBasketCheckout.
   */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

/**
 * Standalone checkout component for drop-in bookings
 * Flow: Dates -> Tickets -> UserForm -> Basket choice
 * No add-ons, single ticket type; buyer details collected at basket checkout
 */
export function CheckoutDropIn({
  course,
  sessions,
  ticketType,
  user,
  bundleID,
  bundle,
  locations = [],
  tutors = [],
  initialSessionId,
  onLoadMoreDates,
  onValidateDiscount,
  onClose,
  onStepChange,
  onJoinWaitlist,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  isSendingMaterial = false,
  school,
  allowPrefillOthersGuestsOnCheckout = false,
  participantsEnabled = false,
  participantLabel = "Attendees",
  participantFields = [],
  existingParticipants = [],
  buyerIsAttendee = false,
  allowUnnamedAttendees = false,
  customer,
  selfSeed,
  onSelfAnswersChange,
  isEmbedMode = false,
  isDirectBooking = false,
  basketItemCount = 0,
  reservedSessionQuantities,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
}: CheckoutDropInProps) {

  // Reuse trial hook since drop-in has the same flow
  // But with isTrial=false to correctly mark as drop-in booking
  const checkout = useCheckoutTrial({
    course,
    sessions,
    ticketType,
    locations,
    user,
    bundleID,
    bundle,
    isSendingMaterial,
    initialSessionId,
    reservedSessionQuantities,
    isTrial: false, // Drop-in, not trial
    allowPrefillOthersGuestsOnCheckout,
    participantsEnabled,
    hasParticipantFields: participantFields.length > 0,
    onLoadMoreDates,
    onValidateDiscount,
  });

  // Handle add to basket
  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket || !checkout.selectedSession) return;

    // Build ticket selections from checkout state
    const ticketSelections = checkout.ticketSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.price || 0,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
          bookingPolicy: ticketType?.bookingPolicy ?? undefined,
        };
      });

    onAddToBasket({
      type: "booking",
      bookingType: "dropIn",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onAddToBasket,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.ticketTypes,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    // A stale attendee list here sent `participants: []` from every
    // direct-checkout host (Oct 2026) — see checkout-regular.
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
  ]);

  // Handle proceed to basket checkout
  const handleProceedToBasketCheckout = useCallback(() => {
    if (!checkout.selectedSession) return;

    // Build ticket selections from checkout state
    const ticketSelections = checkout.ticketSelections
      .filter((sel: any) => sel.quantity > 0)
      .map((sel: any) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.price || 0,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
        };
      });

    onProceedToBasketCheckout({
      type: "booking",
      bookingType: "dropIn",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onProceedToBasketCheckout,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.ticketTypes,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    // A stale attendee list here sent `participants: []` from every
    // direct-checkout host (Oct 2026) — see checkout-regular.
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
  ]);

  const fallbackBookingPolicy = useMemo(
    () => buildFallbackBookingPolicy(termsSettings),
    [termsSettings],
  );

  const handlePolicyClick = useMemo(() => {
    if (!onOpenTermsDialog) return undefined;
    return (ticketName: string, policy: any) => {
      onOpenTermsDialog({
        ticketPolicies: [{ ticketName, policy }],
      });
    };
  }, [onOpenTermsDialog]);

  // Step labels for progress bar
  const stepLabels: Record<string, string> = {
    dates: "Date selection",
    tickets: "Tickets",
    participants: participantLabel,
    userform: "Customer Information",
    personal: "Personal details",
    basketChoice: "Basket",
    payment: "Complete Booking",
  };

  // Progress bar steps
  const progressSteps = checkout.visibleSteps.map((step: string) => ({
    id: step,
    label: stepLabels[step] || step,
    shortLabel:
      {
        dates: "Date",
        tickets: "Tickets",
        participants: participantLabel,
        userform: "Info",
        personal: "Details",
        basketChoice: "Basket",
        payment: "Payment",
      }[step] || step,
  }));

  // Call onStepChange when step changes and scroll to top
  useEffect(() => {
    onStepChange?.(checkout.step);
    // Scroll the checkout's own scroll container to top on step change. Shadow-DOM
    // aware and never scrolls the host window (would yank an embedded page to top).
    setTimeout(() => scrollCheckoutContentToTop(), 50);
  }, [checkout.step, onStepChange]);

  // Format selected date for display
  const formattedDate = (() => {
    if (!checkout.selectedSession) return undefined;

    const session = checkout.selectedSession;
    const tz = timezone || course.timezone;
    // Use session's isOnline if available, fall back to course's isOnline
    const isOnline = session.isOnline ?? course.isOnline ?? false;

    // For online sessions, use user's local timezone; for offline, use course timezone
    const displayTimezone = isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || tz
      : tz;

    // For terms, use the term name as the primary label
    if (session.termName) {
      if (
        session.startDateTime &&
        !isNaN(new Date(session.startDateTime).getTime())
      ) {
        return `${session.termName}, ${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
      }
      return session.termName;
    }

    // For regular sessions, format the date/time
    if (
      session.startDateTime &&
      !isNaN(new Date(session.startDateTime).getTime())
    ) {
      return `${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
    }

    return undefined;
  })();

  // Venue for the sidebar summary and the review step (rationale lives on
  // resolveSummaryLocationName). Plain call — it's a cheap array lookup.
  const summaryLocationLabel = resolveSummaryLocationName(
    course,
    locations,
    checkout.selectedSession,
  );

  // Build ticket items for summary
  const ticketItems = checkout.ticketSelections
    .map((sel: any) => {
      const tt = checkout.ticketTypes.find(
        (t: any) => t.id === sel.ticketTypeId,
      );
      if (!tt || sel.quantity === 0) return null;
      // Use effectivePrice if available (includes any typeCharge adjustments)
      const displayPrice = tt.effectivePrice ?? tt.price;
      return {
        name: tt.name,
        price: displayPrice,
        count: sel.quantity,
        numberOfGuestsPerTicket: tt.numberOfGuestsPerTicket ?? 1,
        bookingPolicy: tt.bookingPolicy ?? null,
      };
    })
    .filter(Boolean) as { name: string; price: number; count: number; numberOfGuestsPerTicket: number; bookingPolicy?: any }[];

  return (
    <ToastProvider>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={course.title}
          subtitle="Drop-in Booking"
          onClose={isDirectBooking ? undefined : onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Progress Bar */}
            <ProgressBar
              steps={progressSteps}
              currentStep={checkout.currentStepIndex}
              onStepClick={(index) => {
                // In direct booking mode, prevent going back to dates step
                if (isDirectBooking && checkout.visibleSteps[index] === "dates") {
                  return;
                }
                checkout.goToStep(checkout.visibleSteps[index]!);
              }}
              className="mb-6"
            />

            {/* Date Selection Step */}
            {checkout.step === "dates" && (
              <DateSelectionStep
                sessions={checkout.sessions}
                selectedSessionId={checkout.selectedSession?.id}
                onSelectSession={(session) => checkout.selectSession(session)}
                hasMoreDates={!checkout.isFullyLoaded}
                onLoadMore={checkout.loadMoreDates}
                isLoading={checkout.isLoading}
                currency={currency}
                timezone={timezone || course.timezone}
                tutors={tutors.map((t: any) => ({
                  id: t._id || t.id,
                  name: t.name,
                  fullName: t.fullName,
                  imageUrl: t.profile?.imageUrl || t.imageUrl,
                }))}
                onJoinWaitlist={onJoinWaitlist}
                schoolId={school?._id}
                isFromPrice={course.priceDisplayMode === "manual" && course.isPriceFrom === true}
                hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
                reservedSessionQuantities={reservedSessionQuantities}
              />
            )}

            {/* Ticket Selection Step */}
            {checkout.step === "tickets" && (
              <TicketSelectionStep
                ticketTypes={checkout.ticketTypes}
                selections={checkout.ticketSelections}
                onSelectionChange={checkout.updateTicketSelection}
                currency={currency}
                totalAvailability={checkout.selectedSession?.availability}
                fallbackBookingPolicy={fallbackBookingPolicy}
                onPolicyClick={handlePolicyClick}
              />
            )}

            {/* User Form Step */}
            {checkout.step === "participants" && (
              <ParticipantsStep
                sessionDate={checkout.selectedSession?.startDateTime}
                participantLabel={participantLabel}
                fields={participantFields}
                existing={existingParticipants}
                maxParticipants={Math.max(1, checkout.totalGuestCount)}
                ticketGroups={checkout.ticketSeatGroups}
                value={checkout.participantsValue}
                onChange={checkout.setParticipantsValue}
                onValidationChange={checkout.setIsParticipantsValid}
                buyerIsAttendee={buyerIsAttendee}
                allowUnnamed={allowUnnamedAttendees}
                buyerSeededRef={checkout.buyerAttendeeSeededRef}
                selfSeed={selfSeed}
                onSelfAnswersChange={onSelfAnswersChange}
                buyer={{
                  // The typed personal-details step wins; the signed-in
                  // customer's own record fills in when this flow skips it
                  // (basket-enabled listings collect the buyer downstream).
                  firstName: checkout.personalDetails.guests[0]?.firstName || customer?.firstName,
                  lastName: checkout.personalDetails.guests[0]?.lastName || customer?.lastName,
                  email: checkout.personalDetails.guests[0]?.email || customer?.email,
                  phone: checkout.personalDetails.guests[0]?.phoneNumber || customer?.phone,
                }}
              />
            )}

            {checkout.step === "userform" && (
              <UserFormStep
                questions={course.userQuestionForm?.questions || []}
                totalGuests={checkout.personalDetails.guests.length}
                collectSystemGuests={checkout.collectSystemGuests}
                participantsEnabled={checkout.showParticipantsStep}
                values={checkout.userFormValues}
                onChange={checkout.setUserFormValues}
                onValidationChange={checkout.setIsUserFormValid}
                mode={course.userQuestionForm?.mode || "all"}
              />
            )}

            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={course.title}
                dateLabel={formattedDate}
                total={checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}

          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={course.title}
              thumbnail={course.images?.[0]?.url}
              description={formattedDate}
              locationLabel={summaryLocationLabel}
              isOnline={course.isOnline}
              availability={checkout.availability}
              hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
              items={ticketItems}
              total={checkout.total}
              currency={currency}
              discount={checkout.discountAmount}
              discountPercentage={checkout.discount?.type === "PERCENTAGE" ? checkout.discount?.value : undefined}
              onApplyDiscount={checkout.applyDiscount}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
              onPolicyClick={handlePolicyClick}
            />

            {/* Drop-in info */}
            <div className="mt-4 p-4 bg-blue-100 dark:bg-blue-900 rounded-lg border border-blue-500">
              <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-1">
                Drop-in Booking
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Book a single session without committing to a full term.
              </p>
            </div>

            {/* Bundle info if using purchased bundle */}
            {bundleID && (
              <div className="mt-4 p-4 bg-green-100 dark:bg-green-900 rounded-lg border border-green-500">
                <h4 className="font-semibold text-green-700 dark:text-green-300 mb-1">
                  Using Bundle
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Payment covered by your purchased bundle.
                </p>
              </div>
            )}
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      {/* Footer - hide continue button on basketChoice step (it has its own action buttons) */}
      <CheckoutFooter
        label="Date Selected"
        value={formattedDate}
        total={checkout.total}
        currency={currency}
        showBack={!checkout.isFirstStep && !(isDirectBooking && checkout.step === "tickets")}
        onBack={checkout.goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={checkout.goToNextStep}
        continueText="Continue"
        continueDisabled={!checkout.canContinue()}
        isEmbedMode={isEmbedMode}
        validationMessage={
          !checkout.canContinue() && checkout.step === "dates"
            ? "Please select a date to continue"
            : undefined
        }
      />
    </ToastProvider>
  );
}
